import { ChildBridge as Bridge, BridgeConfig } from '@smartmediatech/smt-base-bridge';
import RequestMessage from '@smartmediatech/smt-base-bridge/src/model/request-message';
import { Subscription } from 'rxjs';
import jwtDecode from 'jwt-decode';
import AppletError, { DefaultErrors } from '../model/error';
import checkJwtToken from './network/check-jwt-expiration';
import { MessageType, PlatformSocketType } from './network/platform-socket';
import getAccessToken from './network/get-access-token';
import refreshAccessToken from './network/refresh-access-token';
import Log from '../util/log';
import ActionQueue from './util/action-queue';
import checkAllowedDomain from './util/check-allowed-domain';
import { SessionManager } from './session/user-session';
import UserSessionDb from './session/user-session-db';
import UserSessionCookie from './session/user-session-cookie';

type RequestData = {
  endpoint: string;
  method: string;
  headers: Record<string, string>;
  body: string | undefined;
  isFormData: boolean;
  auth: boolean;
};

type WebsocketConnection = {
  ws: PlatformSocketType | undefined;
  subscription: Subscription | undefined;
  isReady: boolean;
};

const viewerApi = [
  'https://api.vl.vatom.network',
  '.smartmediatech.io',
  'https://b.smartmedialabs.io',
  'https://s.smartmedialabs.io',
  'https://g.smartmedialabs.io',
];
const cluApi = [
  'https://cpl.smartmedialabs.io',
  'https://cpl-dev.smartmedialabs.io',
  'https://cpl-b.smartmedialabs.io',
  'https://cpl-s.smartmedialabs.io',
  'https://cpl-g.smartmedialabs.io',
  'https://cpl-l.smartmedialabs.io',
  'https://cpl-dev-b.smartmedialabs.io',
  'https://cpl-dev-s.smartmedialabs.io',
  'https://cpl-dev-g.smartmedialabs.io',
  'https://cpl-dev-l.smartmedialabs.io',
  'https://api.smartmedialabs.io',
];

const isViewer = (path: string) =>
  viewerApi.findIndex((api: string): boolean => path.indexOf(api) >= 0) >= 0;

const isClu = (path: string) =>
  cluApi.findIndex((api: string): boolean => path.indexOf(api) >= 0) >= 0;

class Requestor {
  private bridge: Bridge;

  private isInitialized = false;

  private allowedRequest: string[] = [
    'api.vl.vatom.network',
    '.smartmedialabs.io',
    '.smartmediatech.io',
  ];

  private allowedWebsocket: string[] = ['ws.vatomviewer.com', 'ws.smartmedialabs.io'];

  private websockets: Record<string, WebsocketConnection> = {};

  private restrictedRequestPaths: string[] = ['/v1/access_token'];

  private appId?: string = undefined;

  private platformUrl?: string = undefined;

  private actionQueue: ActionQueue = new ActionQueue();

  private sessionManager: SessionManager;

  private customRequestModifier: Array<{
    test: (path: string) => boolean;
    map: (request: RequestData) => Promise<RequestData>;
  }> = [];

  private customResponseModifier: Array<{
    test: (path: string) => boolean;
    map: (response: Record<string, any>, status: number) => Promise<Record<string, any>>;
  }> = [];

  constructor() {
    const referrer = new URL(document.referrer);
    if (!this.validateReferrer(referrer)) {
      throw new Error('Invalid referrer');
    }

    const bridge = new Bridge({
      meta: {
        version: '1.0.0',
        source: 'smt-platform-requestor',
      },
      origin: referrer.origin,
    } as BridgeConfig);
    this.bridge = bridge;
    Log.info('setting up request applet');

    // Check if we should use cookie-based session manager
    const urlParams = new URLSearchParams(window.location.search);
    const sessionType = urlParams.get('s');
    if (sessionType === 'db') {
      Log.info('Using database session manager');
      this.sessionManager = new UserSessionDb();
    } else {
      Log.info('Using cookie-based session manager');
      this.sessionManager = new UserSessionCookie();
    }
    this.sessionManager
      .init()
      .then(() => this.init())
      .catch((error) => {
        Log.warn(error);
        this.bridge.sendRequest('init.complete', (error as AppletError).toJson());
      });
  }

  private async init(): Promise<void> {
    const referrer = new URL(document.referrer);
    if (!this.validateReferrer(referrer)) {
      throw new Error('Invalid referrer');
    }
    this.customRequestModifier = [
      {
        test: (path: string) => isViewer(path),
        map: (request: RequestData): Promise<RequestData> => {
          const { headers } = request;
          headers['App-Id'] = this.appId ?? '';
          return Promise.resolve({ ...request, headers });
        },
      },
      {
        test: (path: string) => isClu(path) && path.indexOf('/signup') < 0,
        map: async (request: RequestData): Promise<RequestData> => {
          const { body, isFormData } = request;
          let outBody = body;
          if (body && !isFormData) {
            const refreshToken = await this.sessionManager
              .getSession(this.appId ?? '')
              .then((session) => session?.refreshToken);

            if (refreshToken) {
              const jsonBody = JSON.parse(body) as Record<string, any>;
              jsonBody.session_token = refreshToken;
              outBody = JSON.stringify(jsonBody);
            }
          }
          return { ...request, auth: false, body: outBody };
        },
      },
      {
        test: (path: string) => path.indexOf('/v1/user/login') >= 0,
        map: (request: RequestData): Promise<RequestData> => {
          return Promise.resolve({ ...request, auth: false });
        },
      },
    ];
    this.customResponseModifier = [
      {
        test: (path: string): boolean => isViewer(path) && path.indexOf('/api/plaid/') >= 0,
        map: (response: Record<string, any>, status: number): Promise<Record<string, any>> => {
          if (status === 200 && !response.payload) {
            return Promise.resolve({ payload: response });
          }
          return Promise.resolve(response);
        },
      },
      {
        test: (path) => isViewer(path),
        map: (response: Record<string, any>, status: number): Promise<Record<string, any>> => {
          if (!response.isError) {
            if (response.payload === undefined) {
              return Promise.resolve(
                AppletError.createPlatformError({
                  code: response.error || status || 0,
                  status,
                  request_id: response.request_id || '',
                  message: response.message,
                  response,
                }).toJson(),
              );
            }
            const { main } = response.payload as Record<string, any>;
            // Check for main reactor error payload
            if (main && (main as Record<string, any>).error) {
              const mainError: Record<string, any> = (main as Record<string, any>).error;

              return Promise.resolve(
                AppletError.createPlatformError({
                  code: mainError.Code || status || 0,
                  status,
                  request_id: response.request_id || '',
                  message: mainError.Msg || '',
                  response: response.payload,
                }).toJson(),
              );
            }
          }
          return Promise.resolve(response);
        },
      },
      {
        test: (path) => path.indexOf('/v1/user/login') >= 0,
        map: (response: Record<string, any>): Promise<Record<string, any>> => {
          if (!response.isError) {
            const responseTyped = response as { payload: Record<string, any> };
            const { token: refreshToken } = responseTyped.payload.refresh_token;
            delete responseTyped.payload.access_token;
            delete responseTyped.payload.refresh_token;
            return this.sessionManager
              .setSession({ appId: this.appId ?? '', refreshToken: refreshToken || null })
              .then(() => this.validateUserSession(this.sessionManager))
              .then((validation) =>
                this.bridge.sendRequest('session.updated', {
                  isValid: !!validation?.userId,
                  userId: validation?.userId ?? '',
                }),
              )
              .then(() => responseTyped);
          }
          return Promise.resolve(response);
        },
      },
      {
        test: (path: string) => isClu(path),
        map: (response: Record<string, any>, status: number): Promise<Record<string, any>> => {
          if (!response.isError) {
            const responseTyped = response as { session?: Record<string, any> };
            if (responseTyped.session) {
              const { viewer_refresh: refreshToken } = responseTyped.session;
              delete responseTyped.session.viewer_refresh;
              if (refreshToken) {
                return this.sessionManager
                  .setSession({ appId: this.appId ?? '', refreshToken: refreshToken || null })
                  .then(() => this.validateUserSession(this.sessionManager))
                  .then(() => this.validateUserSession(this.sessionManager))
                  .then((validation) =>
                    this.bridge.sendRequest('session.updated', {
                      isValid: !!validation?.userId,
                      userId: validation?.userId ?? '',
                    }),
                  )
                  .then(() => responseTyped);
              }
            }
          }
          return Promise.resolve(response);
        },
      },
      {
        test: (path: string) => isClu(path),
        map: (response: Record<string, any>, status: number): Promise<Record<string, any>> => {
          if (!response.isError) {
            const responseTyped = response as { url?: string };
            if (responseTyped.url) {
              const { url } = responseTyped;

              if (url.indexOf(window.location.hostname) > -1) {
                return Promise.resolve({
                  ...response,
                  url: url.replace(window.location.hostname, referrer.hostname),
                });
              }

              return Promise.resolve(response);
            }
          }
          return Promise.resolve(response);
        },
      },
    ];

    this.bridge.addRequestHandler('session.validate', async (request: RequestMessage) => {
      if (!this.isInitialized) {
        return Promise.resolve(DefaultErrors.INIT_ERROR.toJson());
      }
      return this.validateUserSession(this.sessionManager)
        .then((validation) => ({ isValid: !!validation?.userId, userId: validation?.userId ?? '' }))
        .catch(() => ({ isValid: false }));
    });

    this.bridge.addRequestHandler('session.clear', async (request: RequestMessage) => {
      if (!this.isInitialized) {
        return Promise.resolve(DefaultErrors.INIT_ERROR.toJson());
      }
      return this.clearSession(this.sessionManager).then(() => ({}));
    });

    this.bridge.addRequestHandler('request', (request: RequestMessage) => {
      if (!this.isInitialized) {
        return Promise.resolve(DefaultErrors.INIT_ERROR.toJson());
      }
      const data = request.payload as RequestData;
      if (
        this.restrictedRequestPaths.find((restricted) => data.endpoint.indexOf(restricted) >= 0) ||
        !checkAllowedDomain(data.endpoint, this.allowedRequest)
      ) {
        return Promise.resolve({
          isError: true,
          error: {
            code: 401,
            status: 401,
            requestId: '',
            message: `Unauthorized access : ${data.endpoint}`,
          },
        });
      }

      let transformedRequest = Promise.resolve(data);
      for (let i = 0; i < this.customRequestModifier.length; i += 1) {
        const handler = this.customRequestModifier[i];
        if (handler.test(data.endpoint)) {
          transformedRequest = transformedRequest.then((requestData) =>
            handler.map(requestData).catch((error) => {
              Log.error(error);
              return requestData;
            }),
          );
        }
      }
      return transformedRequest.then((requestData) =>
        this.request(this.sessionManager, requestData),
      );
    });

    this.bridge.addRequestHandler('websocket.open', async (request: RequestMessage) => {
      if (!this.isInitialized) {
        return Promise.resolve(DefaultErrors.INIT_ERROR.toJson());
      }
      const { appId } = this;
      if (appId) {
        const data = request.payload as {
          ws: string;
        };

        if (!checkAllowedDomain(data.ws, this.allowedWebsocket)) {
          return Promise.resolve({
            isError: true,
            error: {
              code: 401,
              status: 401,
              requestId: '',
              message: `Unauthorized access : ${data.ws}`,
            },
          });
        }

        if (!this.websockets[data.ws]) {
          const socketData: WebsocketConnection = {
            ws: undefined,
            subscription: undefined,
            isReady: false,
          };
          this.websockets[data.ws] = socketData;

          await import('./network/platform-socket')
            .then((result) => result.default)
            .then((PlatformSocket) => {
              if (this.websockets[data.ws] === socketData) {
                const platformSocket = new PlatformSocket(
                  appId,
                  data.ws.replace('ws.vatomviewer.com', 'ws.smartmedialabs.io'),
                  () => this.refreshAccessToken(this.sessionManager),
                );
                const subscription = platformSocket.events.subscribe((event) => {
                  switch (event.type) {
                    case MessageType.connect:
                      this.bridge.sendRequest('websocket', { type: 'OPEN', ws: data.ws });
                      break;
                    case MessageType.disconnect:
                      this.bridge.sendRequest('websocket', { type: 'CLOSE', ws: data.ws });
                      break;
                    case MessageType.message:
                      this.bridge.sendRequest('websocket', {
                        type: 'MESSAGE',
                        message: event.data,
                        ws: data.ws,
                      });
                      break;
                    case MessageType.error:
                      this.bridge.sendRequest('websocket', {
                        type: 'ERROR',
                        message: event.data,
                        ws: data.ws,
                      });
                      break;
                    default:
                      break;
                  }
                });
                platformSocket.connect();
                socketData.ws = platformSocket;
                socketData.subscription = subscription;
                socketData.isReady = true;
              }
            });
        }
      }
      return Promise.resolve({});
    });

    this.bridge.addRequestHandler('websocket.close', async (request: RequestMessage) => {
      if (!this.isInitialized) {
        return Promise.resolve(DefaultErrors.INIT_ERROR.toJson());
      }
      const { appId } = this;
      if (appId) {
        const data = request.payload as {
          ws: string;
        };
        if (this.websockets[data.ws]) {
          this.websockets[data.ws].ws?.close();
          this.websockets[data.ws].subscription?.unsubscribe();
          delete this.websockets[data.ws];
        }
      }

      return Promise.resolve({});
    });

    const data = await this.bridge.sendRequest('init', {});

    if (data.app_id) {
      this.appId = data.app_id;
      this.platformUrl = data.platform_url;
      if (data.refresh_token) {
        await this.getAwsWafToken()
          .then((wafToken) =>
            refreshAccessToken(data.refresh_token, data.platform_url, data.app_id, wafToken),
          )
          .then(() =>
            this.sessionManager.setSession({
              appId: this.appId ?? '',
              refreshToken: data.refresh_token || null,
            }),
          )
          .catch(() => {
            Log.info('Invalid refresh token provided on init');
          });
      }
      this.isInitialized = true;
    } else {
      throw new Error('Missing app id or platform url');
    }

    await this.validateUserSession(this.sessionManager)
      .then((validation) => ({
        hasSession: !!validation?.userId,
        userId: validation?.userId ?? '',
      }))
      .then((validation) => this.bridge.sendRequest('init.complete', validation));
  }

  private validateReferrer(referrer: URL): boolean {
    if (referrer.protocol === 'https:') {
      const parts = referrer.hostname.split('.');
      parts.splice(1, 0, 'ra');
      return parts.join('.') === window.location.hostname;
    }
    return false;
  }

  private async validateUserSession(
    sessionManager: SessionManager,
  ): Promise<{ userId: string } | undefined> {
    const { appId } = this;
    if (appId) {
      const refreshToken = await sessionManager
        .getSession(appId)
        .then((session) => session?.refreshToken);
      if (refreshToken && checkJwtToken(refreshToken)) {
        try {
          const dCode: { user_id: string } = jwtDecode(refreshToken);
          return { userId: dCode.user_id };
        } catch (err) {
          return undefined;
        }
      }
    }
    return undefined;
  }

  private request(sessionManager: SessionManager, data: RequestData): Promise<Record<string, any>> {
    return this.actionQueue.pushAction(() =>
      this.getAwsWafToken()
        .then((wafToken) => {
          return (
            data.auth && this.platformUrl
              ? getAccessToken(sessionManager, this.platformUrl ?? '', this.appId, wafToken)
              : Promise.resolve(undefined)
          ).then((accessToken: string | undefined) => {
            const allHeaders: Record<string, string> = {
              ...data.headers,
            };
            if (accessToken) {
              allHeaders['App-Id'] = this.appId ?? '';
              allHeaders.Authorization = `Bearer ${accessToken}`;
            }
            if (wafToken) {
              allHeaders['x-aws-waf-token'] = wafToken;
            }
            let payload: FormData | string | undefined = data.body;
            if (data.body) {
              if (!data.isFormData) {
                allHeaders['Content-Type'] = 'application/json';
              } else {
                const jsonBody: Record<string, any> = JSON.parse(data.body);
                const formData = new FormData();
                Object.keys(jsonBody).forEach((key) => {
                  if (
                    typeof jsonBody[key] === 'string' &&
                    (jsonBody[key] as string).startsWith('file:')
                  ) {
                    const parts = (jsonBody[key] as string).split(';base64,');
                    const contentParts = parts[0].replace('file:', '').split(';');
                    const byteCharacters = atob(parts[1]);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i += 1) {
                      byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: contentParts[1] });
                    formData.append(
                      key,
                      blob,
                      contentParts[0].length > 0 ? contentParts[0] : undefined,
                    );
                  } else formData.append(key, jsonBody[key]);
                });
                payload = formData;
              }
            }
            return fetch(data.endpoint, {
              method: data.method,
              headers: allHeaders,
              body: payload,
              cache: 'no-store',
            })
              .then((response) => {
                if (response.status === 405 && wafToken) {
                  throw DefaultErrors.AWS_WAF_CAPTCHA_REQUIRED;
                } else if (
                  response.status === 403 &&
                  response.headers['Content-Type'] === 'text/html'
                ) {
                  throw DefaultErrors.AWS_WAF_REQUEST_BLOCKED;
                }
                return response.json().then((responseJson: Record<string, any>) => ({
                  json: responseJson,
                  status: response.status,
                }));
              })
              .then(({ json, status }) => {
                let transformedResponse = Promise.resolve(json);
                for (let i = 0; i < this.customResponseModifier.length; i += 1) {
                  const handler = this.customResponseModifier[i];
                  if (handler.test(data.endpoint)) {
                    transformedResponse = transformedResponse.then((response) =>
                      handler.map(response, status).catch((error) => {
                        Log.error(error);
                        return response;
                      }),
                    );
                  }
                }
                return transformedResponse;
              });
          });
        })
        .catch((error) => {
          if (error === DefaultErrors.AWS_WAF_CAPTCHA_REQUIRED) {
            this.actionQueue.pause();
            return this.requestWafCaptcha().then(() => {
              this.actionQueue.resume();
              return this.request(sessionManager, data);
            });
          }
          if (error === DefaultErrors.INVALID_USER_SESSION) {
            this.clearSession(sessionManager);
          }
          throw error;
        }),
    ) as Promise<Record<string, any>>;
  }

  private getAwsWafToken(): Promise<string | undefined> {
    return this.bridge
      .sendRequest('waf.token.get', {})
      .then((result) => result.token as string | undefined);
  }

  private requestWafCaptcha(): Promise<void> {
    return this.bridge.sendRequest('waf.captcha.show', {}).then(() => {});
  }

  private async clearSession(sessionManager: SessionManager): Promise<void> {
    const { appId } = this;
    if (appId) {
      await sessionManager.setSession({ appId, refreshToken: null }).catch((error) => {
        Log.warn(error);
      });
      Object.keys(this.websockets).forEach((key) => {
        this.websockets[key].ws?.close();
        this.websockets[key].subscription?.unsubscribe();
        delete this.websockets[key];
      });
    }
    this.bridge.sendRequest('session.updated', {
      isValid: false,
      userId: '',
    });
  }

  private refreshAccessToken(sessionManager: SessionManager): Promise<string> {
    return this.actionQueue.pushAction(() =>
      this.getAwsWafToken()
        .then((wafToken) =>
          getAccessToken(sessionManager, this.platformUrl ?? '', this.appId, wafToken),
        )
        .catch((error) => {
          if (error === DefaultErrors.AWS_WAF_CAPTCHA_REQUIRED) {
            this.actionQueue.pause();
            return this.requestWafCaptcha().then(() => {
              this.actionQueue.resume();
              return this.refreshAccessToken(sessionManager);
            });
          }
          throw error;
        }),
    ) as Promise<string>;
  }
}

// eslint-disable-next-line no-new
new Requestor();
